import { useDocument } from "@rosette/web_utils/src/ssrHelper.js";
import type { MutableRefObject, ReactNode } from "react";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { BodyPortal } from "@rosette/web_components/src/generic/BodyPortal.js";
import type { MotionProps } from "framer-motion";
import { motion, AnimatePresence } from "framer-motion";
import { fadeInOut } from "@/utils/fadeInOut.js";


interface PopupBaseProps {
    children?: ReactNode;
    visible: boolean;
    close: (reset?: boolean) => void;

    contentsRef: MutableRefObject<HTMLDivElement | null>;
    motionParams?: MotionProps;
}


export function PopupBase({
    children, visible, close,
    contentsRef, motionParams,
}: PopupBaseProps) {
    if (!motionParams) {
        motionParams = fadeInOut();
    }

    const doc = useDocument();

    useEffect(() => {
        if (!visible || !doc)
            return;

        // Close the popup if the user clicks outside of it.
        const listener = (event: MouseEvent) => {
            if (event.defaultPrevented)
                return;

            const contentsElem = contentsRef.current;
            if (!contentsElem)
                return;

            // Check if the event target is within the modal contents.
            // This prevents password managers and other tools from closing
            // the modal when they auto-fill form fields.
            const target = event.target;
            if (target instanceof Node && contentsElem.contains(target))
                return;

            // Fallback to coordinate-based detection for cases where
            // the target isn't a Node.
            const bounds = contentsElem.getBoundingClientRect();
            const withinX = (event.clientX >= bounds.left && event.clientX <= bounds.right);
            const withinY = (event.clientY >= bounds.top && event.clientY <= bounds.bottom);
            if (withinX && withinY)
                return;

            setTimeout(() => close(), 0);
        };

        const options = { capture: false, passive: true };
        doc.addEventListener("click", listener, options);
        return () => {
            doc.removeEventListener("click", listener, options);
        };
    }, [visible, doc, contentsRef, close]);

    const pathName = usePathname();
    const pathNameRef = useRef(pathName);
    useEffect(() => {
        if (pathName !== pathNameRef.current) {
            pathNameRef.current = pathName;
            close();
        }
    }, [pathName, close]);

    return (
        <BodyPortal>
            <AnimatePresence>
                {visible && (
                    <motion.div
                        key="popup_content"
                        className="absolute z-1000 left-0 top-0 w-full"
                        {...motionParams}>

                        {children}
                    </motion.div>
                )}
            </AnimatePresence>
        </BodyPortal>
    );
}
